import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { domain } from '../common/domain';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const Report = () => {
  const [fromDate, setFromDate] = useState(() => {
    // Get today's date by default
    const today = new Date();
    return today;
  });
  const [toDate, setToDate] = useState(() => {
    // Get today's date by default
    const today = new Date();
    return today;
  });
  const [type, setType] = useState('product');
  const [data, setData] = useState([]);
  const [subcategoryGrams, setSubcategoryGrams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalSales: 0,
    totalProfit: 0,
    totalCommission: 0,
    totalItems: 0
  });

  // Check URL parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const startDate = params.get('start_date');
    const endDate = params.get('end_date');
    const reportType = params.get('type');

    if (startDate) setFromDate(new Date(startDate));
    if (endDate) setToDate(new Date(endDate));
    if (reportType) setType(reportType);
  }, []);

  // Format number function moved to top level
  const formatNumber = (num, isAmount = false) => {
    // Convert to string with 2 decimal places
    const formatted = parseFloat(num).toFixed(2);
    // Add single quote prefix for amounts (using actual single quote, not backtick)
    return isAmount ? '₹' + formatted : formatted;
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const fDate = fromDate ? format(fromDate, 'yyyy-MM-dd') : '';
      const tDate = toDate ? format(toDate, 'yyyy-MM-dd') : '';

      let response;
      if (type === 'product') {
        response = await axios.get(domain()+'?endpoint=bills', {
          params: {
            start_date: fDate,
            end_date: tDate,
            type,
          },
        });
        setData(response.data.reports || []);
        
        // Calculate summary statistics for product report
        const stats = response.data.reports.reduce((acc, item) => ({
          totalSales: acc.totalSales + (parseFloat(item.total_amount) || 0),
          totalProfit: acc.totalProfit + (parseFloat(item.total_amount - item.orginal_price) || 0),
          totalCommission: acc.totalCommission + (parseFloat(item.commission_amount) || 0),
          totalItems: acc.totalItems + 1
        }), { totalSales: 0, totalProfit: 0, totalCommission: 0, totalItems: 0 });
        setSummary(stats);
      } else if (type === 'purchase') {
        response = await axios.get(domain()+'?endpoint=purchase_report');
        if (response.data.success) {
          setData(response.data.products || []);
          setSubcategoryGrams(response.data.subcategory_totals || []);
        }
        setSummary({ totalSales: 0, totalProfit: 0, totalCommission: 0, totalItems: 0 });
      } else if (type === 'commission') {
        response = await axios.get(domain() + '?endpoint=bills', {
          params: {
            start_date: fDate,
            end_date: tDate,
            type: 'bill'
          },
        });
        
        // Group data by salesperson
        const billData = response.data.reports || [];
        const groupedData = {};

        billData.forEach(bill => {
          const salespersonNames = bill.salesperson_names.split(',');
          const numSalespersons = salespersonNames.length;
          
          salespersonNames.forEach(name => {
            const trimmedName = name.trim();
            if (!groupedData[trimmedName]) {
              groupedData[trimmedName] = {
                salesperson_name: trimmedName,
                total_sales: 0,
                total_profit: 0,
                total_commission: 0,
                items_sold: 0,
                bill_references: new Set()
              };
            }
            
            // Split amounts equally among salespersons
            groupedData[trimmedName].total_sales += parseFloat(bill.bill_total || 0) / numSalespersons;
            groupedData[trimmedName].total_profit += parseFloat(bill.total_profit || 0) / numSalespersons;
            groupedData[trimmedName].total_commission += parseFloat(bill.total_commission || 0) / numSalespersons;
            groupedData[trimmedName].items_sold += parseInt(bill.items_count || 0) / numSalespersons;
            groupedData[trimmedName].bill_references.add(bill.bill_ref_id);
          });
        });

        // Convert bill references to string and prepare final data
        const processedData = Object.values(groupedData).map(item => ({
          ...item,
          bill_references: Array.from(item.bill_references).join(', ')
        }));

        setData(processedData);
        
        // Calculate overall summary
        const stats = processedData.reduce((acc, item) => ({
          totalSales: acc.totalSales + item.total_sales,
          totalProfit: acc.totalProfit + item.total_profit,
          totalCommission: acc.totalCommission + item.total_commission,
          totalItems: acc.totalItems + item.items_sold
        }), { totalSales: 0, totalProfit: 0, totalCommission: 0, totalItems: 0 });
        
        setSummary(stats);
      }
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setData([]);
      setSubcategoryGrams([]);
      setSummary({ totalSales: 0, totalProfit: 0, totalCommission: 0, totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [type, fromDate, toDate]);

  const exportExcel = () => {
    if (data.length === 0) {
      alert('No data to export!');
      return;
    }
  
    const exportData = type === 'product' ? 
      data.map(item => ({
        "Product Name": item.product_name,
        "Rate": item.rate,
        "Weight": item.weight,
        "Total Amount": item.total_amount,
        "Original Price": item.orginal_price,
        "Profit Amount": item.total_amount - item.orginal_price,
        "Salesperson Name": item.salesperson_name,
        "Commission Amount": item.commission_amount
      })) :
      type === 'commission' ?
      data.map(item => ({
        "Salesperson Name": item.salesperson_name,
        "Total Sales": item.total_sales,
        "Total Profit": item.total_profit,
        "Total Commission": item.total_commission,
        "Items Sold": item.items_sold.toString()
      })) :
      data.map(item => ({
        "Product Name": item.name,
        "Category": item.category_name,
        "Current Quantity": item.quantity,
        "Total Weight": item.total_weight,
        "Available Weight": item.available_weight,
        "Purity": item.purity_name
      }));

    // Add summary row for product report
    if (type === 'product') {
      exportData.push({}, {
        "Product Name": "SUMMARY",
        "Total Amount": summary.totalSales,
        "Profit Amount": summary.totalProfit,
        "Commission Amount": summary.totalCommission,
        "Total Items": summary.totalItems
      });
    } else if (type === 'commission') {
      exportData.push({}, {
        "Salesperson Name": "SUMMARY",
        "Total Sales": summary.totalSales,
        "Total Profit": summary.totalProfit,
        "Total Commission": summary.totalCommission,
        "Items Sold": summary.totalItems.toString()
      });
    }
  
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `report_${format(new Date(), "yyyyMMdd_HHmmss")}.xlsx`);
  };

  const exportPDF = () => {
    if (data.length === 0) {
      alert('No data to export!');
      return;
    }

    const doc = new jsPDF();
    const title = type === 'product' ? 'Product Sales Report' : type === 'commission' ? 'Salesperson Commission Report' : 'Purchase Report';
    
    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 15);
    
    // Add date range if available
    if (fromDate && toDate) {
      doc.setFontSize(10);
      doc.text(`Date Range: ${format(fromDate, 'dd-MM-yyyy')} to ${format(toDate, 'dd-MM-yyyy')}`, 14, 22);
    }

    // Prepare table data
    const tableData = type === 'product' ? 
      data.map(item => [
        item.product_name,
        formatNumber(item.rate, true),
        `${item.weight}g`,
        formatNumber(item.total_amount, true),
        formatNumber(item.orginal_price, true),
        formatNumber(item.total_amount - item.orginal_price, true),
        item.salesperson_name,
        formatNumber(item.commission_amount, true)
      ]) : type === 'commission' ?
      data.map(item => [
        item.salesperson_name,
        formatNumber(item.total_sales, true),
        formatNumber(item.total_profit, true),
        formatNumber(item.total_commission, true),
        item.items_sold.toString()
      ]) :
      data.map(item => [
        item.name,
        item.category_name,
        item.quantity,
        `${formatNumber(item.total_weight)}g`,
        `${formatNumber(item.available_weight)}g`,
        item.purity_name
      ]);

    // Add summary for product and commission reports
    if (type === 'product') {
      tableData.push([]);
      tableData.push([
        'SUMMARY',
        '',
        '',
        formatNumber(summary.totalSales, true),
        '',
        formatNumber(summary.totalProfit, true),
        '',
        formatNumber(summary.totalCommission, true)
      ]);
    } else if (type === 'commission') {
      tableData.push([]);
      tableData.push([
        'SUMMARY',
        formatNumber(summary.totalSales, true),
        formatNumber(summary.totalProfit, true),
        formatNumber(summary.totalCommission, true),
        summary.totalItems.toString()
      ]);
    }

    // Define table headers
    const headers = type === 'product' ? 
      ['Product Name', 'Rate', 'Weight', 'Total Amount', 'Original Price', 'Profit Amount', 'Salesperson', 'Commission'] :
      type === 'commission' ?
      ['Salesperson Name', 'Total Sales', 'Total Profit', 'Total Commission', 'Items Sold'] :
      ['Product Name', 'Category', 'Current Quantity', 'Total Weight', 'Available Weight', 'Purity'];

    // Calculate column widths based on report type
    const columnStyles = type === 'product' ? {
      0: { cellWidth: 'auto' }, // Product Name
      1: { cellWidth: 'auto', halign: 'right' }, // Rate
      2: { cellWidth: 'auto', halign: 'right' }, // Weight
      3: { cellWidth: 'auto', halign: 'right' }, // Total Amount
      4: { cellWidth: 'auto', halign: 'right' }, // Original Price
      5: { cellWidth: 'auto', halign: 'right' }, // Profit Amount
      6: { cellWidth: 'auto' }, // Salesperson
      7: { cellWidth: 'auto', halign: 'right' } // Commission
    } : type === 'commission' ? {
      0: { cellWidth: 'auto' }, // Salesperson Name
      1: { cellWidth: 'auto', halign: 'right' }, // Total Sales
      2: { cellWidth: 'auto', halign: 'right' }, // Total Profit
      3: { cellWidth: 'auto', halign: 'right' }, // Total Commission
      4: { cellWidth: 'auto', halign: 'right' } // Items Sold
    } : {
      0: { cellWidth: 'auto' }, // Product Name
      1: { cellWidth: 'auto' }, // Category
      2: { cellWidth: 'auto', halign: 'right' }, // Current Quantity
      3: { cellWidth: 'auto', halign: 'right' }, // Total Weight
      4: { cellWidth: 'auto', halign: 'right' }, // Available Weight
      5: { cellWidth: 'auto' } // Purity
    };

    // Add table
    doc.autoTable({
      head: [headers],
      body: tableData,
      startY: 25,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        lineWidth: 0.1,
        font: 'helvetica',
        fontStyle: 'normal',
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      columnStyles,
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 10, right: 10 },
      tableWidth: 'auto'
    });

    // Save the PDF
    doc.save(`report_${format(new Date(), "yyyyMMdd_HHmmss")}.pdf`);
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-4">
        {type === 'product' ? 'Product Sales Report' : 
         type === 'commission' ? 'Salesperson Commission Report' :
         'Purchase Report'}
      </h3>
      
      {/* Summary Cards - Only for product and commission reports */}
      {(type === 'product' || type === 'commission') && data.length > 0 && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <h5 className="card-title">Total Sales</h5>
                <h3>₹{summary.totalSales.toFixed(2)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <h5 className="card-title">Total Profit</h5>
                <h3>₹{summary.totalProfit.toFixed(2)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <h5 className="card-title">Total Commission</h5>
                <h3>₹{summary.totalCommission.toFixed(2)}</h3>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body">
                <h5 className="card-title">Total Items</h5>
                <h3>{summary.totalItems}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="row g-3 my-3">
        <div className="col-md-3 d-flex flex-column">
          <label>From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="dd-MM-yyyy"
            className="form-control"
            placeholderText="Select from date"
          />
        </div>
        <div className="col-md-3 d-flex flex-column">
          <label>To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="dd-MM-yyyy"
            className="form-control"
            placeholderText="Select to date"
          />
        </div>
        <div className="col-md-2">
          <label>Report Type</label>
          <select className="form-control" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="product">Product Report</option>
            <option value="purchase">Purchase Report</option>
            <option value="commission">Commission Report</option>
          </select>
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-success w-100" onClick={exportExcel}>Export to Excel</button>
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-danger w-100" onClick={exportPDF}>Export to PDF</button>
        </div>
      </div>

      {/* Subcategory Grams Table */}
      {type === 'purchase' && subcategoryGrams.length > 0 && (
        <div className="card mb-4">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">Subcategory-wise Grams</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Subcategory</th>
                    <th>Total Grams</th>
                  </tr>
                </thead>
                <tbody>
                  {subcategoryGrams.map((item, index) => (
                    <tr key={index}>
                      <td>{item.subcategory_name}</td>
                      <td>{formatNumber(item.total_grams)} g</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-primary">
                    <td className="text-end fw-bold">Total Grams:</td>
                    <td className="fw-bold">
                      {formatNumber(subcategoryGrams.reduce((sum, item) => sum + item.total_grams, 0))} g
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Main Data Table */}
      <div className="table-responsive mt-3">
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading data...</p>
          </div>
        ) : (
          <table className="table table-bordered table-hover">
            <thead className="table-light">
              {type === 'product' ? (
                <tr>
                  <th>Product Name</th>
                  <th>Rate</th>
                  <th>Weight</th>
                  <th>Total Amount</th>
                  <th>Original Price</th>
                  <th>Profit Amount</th>
                  <th>Salesperson</th>
                  <th>Commission</th>
                </tr>
              ) : type === 'commission' ? (
                <tr>
                  <th>Salesperson Name</th>
                  <th>Total Sales</th>
                  <th>Total Profit</th>
                  <th>Total Commission</th>
                  <th>Items Sold</th>
                </tr>
              ) : (
                <tr>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>Current Quantity</th>
                  <th>Total Weight</th>
                  <th>Available Weight</th>
                  <th>Purity</th>
                </tr>
              )}
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={type === 'product' ? 8 : type === 'commission' ? 5 : 7} className="text-center">
                    No data found for the selected period
                  </td>
                </tr>
              ) : (
                data.map((item, i) => (
                  <tr key={i}>
                    {type === 'product' ? (
                      <>
                        <td>{item.product_name}</td>
                        <td>{formatNumber(item.rate, true)}</td>
                        <td>{item.weight}g</td>
                        <td>{formatNumber(item.total_amount, true)}</td>
                        <td>{formatNumber(item.orginal_price, true)}</td>
                        <td>{formatNumber(item.total_amount - item.orginal_price, true)}</td>
                        <td>{item.salesperson_name}</td>
                        <td>{formatNumber(item.commission_amount, true)}</td>
                      </>
                    ) : type === 'commission' ? (
                      <>
                        <td>{item.salesperson_name}</td>
                        <td>{formatNumber(item.total_sales, true)}</td>
                        <td>{formatNumber(item.total_profit, true)}</td>
                        <td>{formatNumber(item.total_commission, true)}</td>
                        <td>{item.items_sold}</td>
                      </>
                    ) : (
                      <>
                        <td>{item.name}</td>
                        <td>{item.category_name}</td>
                        <td>{item.quantity}</td>
                        <td>{item.total_weight}g</td>
                        <td>{item.available_weight}g</td>
                        <td>{item.purity_name}</td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Report;
